import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { usersService } from '@/services/apiClient';
import type {
  NotificationPreferences,
  ToggleUserActiveData,
  UpdateUserData,
  UsersQuery,
} from '@/types/user';

import { analyticsKeys } from './useAnalytics';

/**
 * Users Hooks
 * React Query hooks for /api/v1/users/* endpoints
 */

// Query keys
export const userKeys = {
  all: ['users'] as const,
  detail: (userId: string) => [...userKeys.all, 'detail', userId] as const,
  details: (userId: string, organizationId?: string) =>
    [...userKeys.all, 'details', userId, organizationId ?? null] as const,
  list: (query?: UsersQuery) => [...userKeys.all, 'list', query] as const,
  withoutOrganization: () => [...userKeys.all, 'without-organization'] as const,
};

/**
 * Hook to fetch user by ID (basic info)
 */
export const useUser = (userId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: userKeys.detail(userId || ''),
    queryFn: () => usersService.getUser(userId || ''),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch user details (full user information)
 */
export const useUserDetails = (userId: string | undefined, organizationId?: string) => {
  return useQuery({
    queryKey: userKeys.details(userId || '', organizationId),
    queryFn: () => usersService.getUserDetails(userId || '', organizationId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch paginated users with filters
 */
export const useUsers = (query?: UsersQuery) => {
  return useQuery({
    queryKey: userKeys.list(query),
    queryFn: () => usersService.getUsers(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch all users without an organization (SUPER_ADMIN only)
 */
export const useUsersWithoutOrganization = () => {
  return useQuery({
    queryKey: userKeys.withoutOrganization(),
    queryFn: () => usersService.getUsersWithoutOrganization(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to update a user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserData }) =>
      usersService.updateUser(userId, data),
    onSuccess: () => {
      // Invalidate analytics and user queries
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.employeeAnalytics() });
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.organizationInsights() });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

/**
 * Hook to toggle user active status
 */
export const useToggleUserActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: ToggleUserActiveData }) =>
      usersService.toggleUserActive(userId, data),
    onSuccess: () => {
      // Invalidate analytics and user queries
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.employeeAnalytics() });
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.organizationInsights() });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

/**
 * Hook to delete a user
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.deleteUser(userId),
    onSuccess: () => {
      // Invalidate analytics and user queries
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.employeeAnalytics() });
      void queryClient.invalidateQueries({ queryKey: analyticsKeys.organizationInsights() });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
};

/**
 * Hook to update notification preferences
 */
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      preferences,
    }: {
      userId: string;
      preferences: NotificationPreferences;
    }) => usersService.updateNotificationPreferences(userId, preferences),
    onSuccess: (_, variables) => {
      // Invalidate user queries to refetch updated preferences
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.userId) });
      void queryClient.invalidateQueries({ queryKey: userKeys.details(variables.userId) });
    },
  });
};
